/**
 * Environment-aware logging utility
 * Replaces direct console.log/warn/error calls with controlled logging
 */

const LOG_LEVELS = {
  ERROR: "error",
  WARN: "warn",
  INFO: "info",
  DEBUG: "debug",
};

const isDevelopment = process.env.NODE_ENV === "development";
const isTest = process.env.NODE_ENV === "test";

/**
 * Get the current log level from environment or default
 */
const getLogLevel = () => {
  return process.env.REACT_APP_LOG_LEVEL || (isDevelopment ? "debug" : "error");
};

/**
 * Check if a log level should be displayed
 */
const shouldLog = (level) => {
  const currentLevel = getLogLevel();
  const levels = Object.values(LOG_LEVELS);
  const currentIndex = levels.indexOf(currentLevel);
  const messageIndex = levels.indexOf(level);

  return messageIndex <= currentIndex;
};

/**
 * Format log message with timestamp and context
 */
const formatMessage = (level, message, context = {}) => {
  const timestamp = new Date().toISOString();
  const contextStr =
    Object.keys(context).length > 0 ? JSON.stringify(context) : "";

  return {
    timestamp,
    level,
    message,
    context,
    formatted: `[${timestamp}] [${level.toUpperCase()}] ${message} ${contextStr}`,
  };
};

/**
 * Send log to external service in production
 */
const sendToExternalService = (logData) => {
  // In production, send to logging service (e.g., Sentry, LogRocket, CloudWatch)
  if (window.errorTracker && logData.level === LOG_LEVELS.ERROR) {
    window.errorTracker.captureMessage(logData.message, {
      level: logData.level,
      extra: logData.context,
    });
  }
};

/**
 * Logger object with methods for each log level
 */
const logger = {
  error: (message, context = {}) => {
    const logData = formatMessage(LOG_LEVELS.ERROR, message, context);

    if (shouldLog(LOG_LEVELS.ERROR)) {
      if (isDevelopment || isTest) {
        console.error(logData.formatted, logData.context);
      } else {
        sendToExternalService(logData);
      }
    }

    return logData;
  },

  warn: (message, context = {}) => {
    const logData = formatMessage(LOG_LEVELS.WARN, message, context);

    if (shouldLog(LOG_LEVELS.WARN)) {
      if (isDevelopment || isTest) {
        console.warn(logData.formatted, logData.context);
      } else {
        // Note: sendToExternalService only sends ERROR level to external services
        // WARN logs in production are intentionally not sent to reduce noise
        // If you need WARN level tracking in production, update sendToExternalService
        sendToExternalService(logData);
      }
    }

    return logData;
  },

  info: (message, context = {}) => {
    const logData = formatMessage(LOG_LEVELS.INFO, message, context);

    if (shouldLog(LOG_LEVELS.INFO)) {
      if (isDevelopment || isTest) {
        console.info(logData.formatted, logData.context);
      }
    }

    return logData;
  },

  debug: (message, context = {}) => {
    const logData = formatMessage(LOG_LEVELS.DEBUG, message, context);

    if (shouldLog(LOG_LEVELS.DEBUG) && isDevelopment) {
      console.log(logData.formatted, logData.context);
    }

    return logData;
  },

  /**
   * Group related logs together
   */
  group: (label, callback) => {
    if (isDevelopment && shouldLog(LOG_LEVELS.DEBUG)) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  },

  /**
   * Time a function execution
   */
  time: (label) => {
    if (isDevelopment && shouldLog(LOG_LEVELS.DEBUG)) {
      console.time(label);
    }
  },

  timeEnd: (label) => {
    if (isDevelopment && shouldLog(LOG_LEVELS.DEBUG)) {
      console.timeEnd(label);
    }
  },
};

export default logger;
